// ===================================================================
// KAIXO - EmailVerifyModal (post-registro: Turnstile + Email OTP)
// ===================================================================
// Decisión Víctor 2026-05-06: modal nuevo intermedio entre Login (registro
// inicial) y la app autenticada. Maneja:
//   1. Cloudflare Turnstile — challenge invisible 99% del tiempo
//   2. Email verify code (Resend OTP 6 dígitos)
//
// Razón modal separado: el formulario de registro Login.js ya es largo.
// Mejor 1 pantalla rápida focus en humano + verifica email, que inflar
// el formulario existente.
//
// Flujo:
//   1. User crea cuenta en Login (POST /api/users/test-create) → success
//   2. Login.handleRegister abre <EmailVerifyModal email=... onVerified=... />
//   3. EmailVerifyModal:
//      a. Llama POST /api/auth/email-verify-request → backend envía email
//      b. Render Turnstile invisible (1-2s auto-resuelve)
//      c. Input código 6 dígitos del email
//      d. POST /api/auth/email-verify-confirm con turnstileToken + code
//      e. Si success → onVerified(user) → Login finaliza login (localStorage + redirect)
// ===================================================================

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import './EmailVerifyModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-dlmm.onrender.com/api';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAADKQIOYaFp0gnPhu';

const I18N = {
    español: {
        title: 'Verifica tu cuenta',
        subtitle: '2 pasos rápidos para activar KAIXO',
        step1Label: 'Paso 1 · Verificación de seguridad',
        step1Hint: 'Cloudflare comprueba que eres humano. Suele tardar 1-2 segundos.',
        step1OK: '✓ Verificado',
        step2Label: 'Paso 2 · Confirma tu correo',
        step2Hint: (email) => `Te hemos enviado un código de 6 dígitos a ${email}. Introdúcelo abajo:`,
        confirmBtn: 'Confirmar',
        resendBtn: 'Reenviar código',
        resendOk: 'Código reenviado.',
        resendCooldown: (s) => `Espera ${s}s para reenviar`,
        cancelBtn: 'Cancelar',
        sending: 'Enviando código...',
        confirming: 'Confirmando...',
        privacyNote: 'Cero anuncios al correo.',
        privacyLink: 'Política de privacidad',
        errorMissingCode: 'Introduce el código de 6 dígitos',
        errorMissingTurnstile: 'Espera a que termine la verificación de seguridad'
    },
    inglés: {
        title: 'Verify your account',
        subtitle: '2 quick steps to activate KAIXO',
        step1Label: 'Step 1 · Security check',
        step1Hint: 'Cloudflare is checking you are human. Usually takes 1-2 seconds.',
        step1OK: '✓ Verified',
        step2Label: 'Step 2 · Confirm your email',
        step2Hint: (email) => `We sent a 6-digit code to ${email}. Enter it below:`,
        confirmBtn: 'Confirm',
        resendBtn: 'Resend code',
        resendOk: 'Code resent.',
        resendCooldown: (s) => `Wait ${s}s to resend`,
        cancelBtn: 'Cancel',
        sending: 'Sending code...',
        confirming: 'Confirming...',
        privacyNote: 'Zero marketing emails.',
        privacyLink: 'Privacy Policy',
        errorMissingCode: 'Enter the 6-digit code',
        errorMissingTurnstile: 'Wait for the security check to finish'
    }
};

function pickL(language) {
    if (language && I18N[language]) return I18N[language];
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return I18N[u?.language] || I18N.español;
    } catch { return I18N.español; }
}

function EmailVerifyModal({ email, onVerified, onCancel, language }) {
    const L = pickL(language);

    const [turnstileToken, setTurnstileToken] = useState(null);
    // Si el widget Turnstile falla (red bloqueada, Cloudflare lento, error
    // del CDN), permitimos al usuario continuar. El backend tiene fail-open
    // configurado y rate-limit por IP, así que la pérdida de seguridad es
    // mínima y evitamos bloquear a un usuario legítimo por algo del lado
    // externo. Caso real reportado 2026-05-20: red móvil de Perú bloqueando
    // challenges.cloudflare.com.
    const [turnstileError, setTurnstileError] = useState(false);
    const [code, setCode] = useState('');
    const [sending, setSending] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');

    const sentOnceRef = useRef(false);

    useEffect(() => {
        if (sentOnceRef.current) return;
        sentOnceRef.current = true;
        sendCode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setTimeout(() => setResendCooldown(s => s - 1), 1000);
        return () => clearTimeout(t);
    }, [resendCooldown]);

    async function sendCode() {
        setSending(true);
        setError('');
        try {
            const r = await fetch(`${API_URL}/auth/email-verify-request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    language: language?.startsWith?.('en') || language === 'inglés' ? 'en' : 'es'
                })
            });
            const data = await r.json();
            if (data.success) {
                setInfo(L.resendOk);
                setResendCooldown(60);
            } else {
                setError(data.error || 'No pudimos enviar el código');
            }
        } catch (err) {
            setError('Error de red. Inténtalo otra vez.');
        }
        setSending(false);
    }

    async function handleConfirm() {
        setError('');
        setInfo('');
        // Si el captcha falló (red bloqueando Cloudflare), aceptamos pasar
        // sin token. El backend hace fail-open de todos modos.
        if (!turnstileToken && !turnstileError) { setError(L.errorMissingTurnstile); return; }
        if (!/^\d{6}$/.test(code.trim())) { setError(L.errorMissingCode); return; }
        setConfirming(true);
        try {
            const r = await fetch(`${API_URL}/auth/email-verify-confirm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    code: code.trim(),
                    turnstileToken
                })
            });
            const data = await r.json();
            if (data.success && data.verified) {
                if (onVerified) onVerified(data.user);
            } else {
                setError(data.error || data.message || 'Código incorrecto');
            }
        } catch (err) {
            setError('Error de red. Inténtalo otra vez.');
        }
        setConfirming(false);
    }

    return ReactDOM.createPortal(
        <div className="evm-overlay" onClick={onCancel}>
            <div className="evm-modal" onClick={e => e.stopPropagation()}>
                <div className="evm-head">
                    <h2>{L.title}</h2>
                    <p>{L.subtitle}</p>
                </div>

                {/* Paso 1 — Turnstile (invisible 99% del tiempo) */}
                <div className="evm-step">
                    <div className="evm-step-label">{L.step1Label}</div>
                    {turnstileToken ? (
                        <div className="evm-step-ok">{L.step1OK}</div>
                    ) : turnstileError ? (
                        <div className="evm-step-warn">
                            No fue posible cargar la verificación de seguridad (red bloqueada o Cloudflare lento). Puedes continuar igualmente.
                        </div>
                    ) : (
                        <>
                            <div className="evm-turnstile">
                                <Turnstile
                                    siteKey={TURNSTILE_SITE_KEY}
                                    options={{ theme: 'light', size: 'normal' }}
                                    onSuccess={token => { setTurnstileToken(token); setTurnstileError(false); }}
                                    onError={() => setTurnstileError(true)}
                                    onExpire={() => setTurnstileToken(null)}
                                />
                            </div>
                            <p className="evm-hint">{L.step1Hint}</p>
                        </>
                    )}
                </div>

                {/* Paso 2 — Email code */}
                <div className="evm-step">
                    <div className="evm-step-label">{L.step2Label}</div>
                    <p className="evm-hint">{L.step2Hint(email)}</p>
                    <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d{6}"
                        maxLength={6}
                        autoComplete="one-time-code"
                        className="evm-code-input"
                        placeholder="123456"
                        value={code}
                        onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        autoFocus
                    />
                    <div className="evm-resend-row">
                        {resendCooldown > 0 ? (
                            <span className="evm-resend-cooldown">{L.resendCooldown(resendCooldown)}</span>
                        ) : (
                            <button className="evm-resend-btn" onClick={sendCode} disabled={sending}>
                                {sending ? L.sending : L.resendBtn}
                            </button>
                        )}
                    </div>
                </div>

                {error && <div className="evm-error">{error}</div>}
                {info && !error && <div className="evm-info">{info}</div>}

                <div className="evm-privacy">
                    <strong>{L.privacyNote}</strong>{' '}
                    <a href="https://kaixo.ai/privacy.html" target="_blank" rel="noopener noreferrer">
                        {L.privacyLink}
                    </a>
                </div>

                <div className="evm-actions">
                    <button className="evm-btn-cancel" onClick={onCancel}>{L.cancelBtn}</button>
                    <button
                        className="evm-btn-confirm"
                        onClick={handleConfirm}
                        disabled={confirming || code.length !== 6 || (!turnstileToken && !turnstileError)}
                    >
                        {confirming ? L.confirming : L.confirmBtn}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default EmailVerifyModal;
